import { useCallback, useMemo, useState } from "react";
import {
  PDFReportChartType,
  PDFReportValueType,
  ProtectThemedCardValues,
  ReportThemedCardConfig,
  ReportValueCardProps,
} from "helpers/types";
import { SingleValueCardType } from "constants/index";
import {
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardValuesLabels,
} from "helpers/constants";
import { toFiniteNumber } from "components/ui/formatters";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { useMaxDropLimits } from "hooks/useMaxDropLimits";
import { NO_REPORT_DISPLAY } from "../reportFieldStates";
import { useAvailableMarginBalance } from "../useAvailableMarginBalance";
import { STRATEGY_REPORT_DEFAULTS } from "./strategyReportDefaults";
import HeaderAssetSelect from "./HeaderAssetSelect";

// The Protect card's per-asset max-drop dictionary from the report response.
type ProtectDrops = ProtectThemedCardValues["max_stock_drop"];

const DRAWDOWN_CHART_TITLE = "Drawdown Comparison";
const CHART_SERIES_STOCK = "Stock";
const CHART_SERIES_OEA = "OEA";

/**
 * The Protect card: its four rows, the asset picker in its header, and the
 * drawdown chart's y-axis bounds.
 *
 * The response returns the max stock drop as a dictionary keyed by symbol,
 * and the asset picked in the header select drives which asset's drop the
 * row (and the drawdown chart) shows.
 */
export const useProtectCard = () => {
  const [maxStockDrop, setMaxStockDrop] = useStrategyReportValue(
    "max_stock_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.MAX_STOCK_DROP,
  );
  const [oeaDrop, setOeaDrop] = useStrategyReportValue(
    "oea_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.OEA_DROP,
  );
  const [collarPL, setCollarPL] = useStrategyReportValue(
    "collar_pl_strategy",
    STRATEGY_REPORT_DEFAULTS.COLLAR_PL,
  );
  // Available Margin Balance: the one field on this report that is neither
  // seeded nor persisted, and the one whose default is a STATE rather than a
  // figure -- N/A until somebody types into it. Its whole state machine, and
  // why, lives in useAvailableMarginBalance.
  const {
    row: availableMarginBalanceRow,
    onValueChange: handleAvailableMarginBalanceChange,
    reset: resetAvailableMarginBalance,
  } = useAvailableMarginBalance();

  const [protectDrops, setProtectDrops] = useState<ProtectDrops | null>(null);
  const [selectedProtectAsset, setSelectedProtectAsset] = useState("");

  // The drawdown chart's two bars, read once: null when the row holds no
  // figure, so the PDF entries below say "No Report" rather than zero.
  // `parseFloat(...) || 0` stood at each of those two entries and put a 0.0%
  // bar on a client's paper for every "No Report" row -- a portfolio that did
  // not fall at all, printed as a result. The screen chart says the same
  // state (drawdownRowFigure).
  const maxStockDropPct = toFiniteNumber(maxStockDrop);
  const oeaDropPct = toFiniteNumber(oeaDrop);

  // The chart's y-axis bounds, derived from THIS protect asset's own two
  // drops and stored per asset (OAM-648) -- a stock that fell 60% and one
  // that fell 3% no longer share one 20% axis. The readings above are what
  // the derivation sees, so a row with no figure takes no part in it rather
  // than standing in it as a 0.
  const [maxDropLimits, setMaxDropLimits] = useMaxDropLimits(
    selectedProtectAsset,
    maxStockDropPct,
    oeaDropPct,
  );

  // The PDF chart's bounds. Null with no figure and no override -- the
  // renderer then scales nothing, because there is nothing to draw (task
  // 4264). Read out as numbers because the limits object is rebuilt on every
  // render, and the card below is memoised on them.
  const maxDropLimit = maxDropLimits?.maxDrop ?? null;
  const negativeDropLimit = maxDropLimits?.negativeLimit ?? null;

  const applyProtectDrop = useCallback(
    (drops: ProtectDrops, symbol: string) => {
      setMaxStockDrop(drops[symbol] ?? 0);
    },
    [setMaxStockDrop],
  );

  const handleProtectAssetChange = useCallback(
    (symbol: string) => {
      setSelectedProtectAsset(symbol);
      if (protectDrops) applyProtectDrop(protectDrops, symbol);
    },
    [protectDrops, applyProtectDrop],
  );

  // Applies a completed run's Protect payload. oea_drop is null when no
  // bucket has daily values, and the stored value stays in that case.
  // available_margin_balance is deliberately NOT applied: the row is
  // hand-entered (useAvailableMarginBalance), so the payload's reserved zero
  // would overwrite an operator's typed figure with a number no calculation
  // produced.
  const applyRun = useCallback(
    (protect: ProtectThemedCardValues | undefined) => {
      if (protect) {
        if (protect.oea_drop != null) {
          setOeaDrop(protect.oea_drop);
        }
        setCollarPL(protect.collar_pl);
      }
      const drops = protect?.max_stock_drop ?? {};
      const dropSymbols = Object.keys(drops);
      setProtectDrops(dropSymbols.length ? drops : null);
      if (dropSymbols.length) {
        // Keep the previously picked asset when the new results still
        // carry it; otherwise default to the bucket's main asset, then
        // the first one.
        const assetToShow = dropSymbols.includes(selectedProtectAsset)
          ? selectedProtectAsset
          : (protect?.main_symbol ?? dropSymbols[0]);
        setSelectedProtectAsset(assetToShow);
        applyProtectDrop(drops, assetToShow);
      }
    },
    [setOeaDrop, setCollarPL, selectedProtectAsset, applyProtectDrop],
  );

  // A change of report inputs retires the run on screen.
  //
  // Available Margin Balance is hand-entered rather than calculated, but it
  // goes too: it is the ONE hand-entered row that reaches the client-facing
  // PDF as a figure, so a number typed for the previous client would print
  // under this one's name with nothing on the page to say it was not theirs.
  // Back to N/A instead, which takes the line off the export too. A report
  // RE-RUN for the same client and period does not come through here, so a
  // typed figure survives that.
  const resetForNewInputs = useCallback(() => {
    setProtectDrops(null);
    resetAvailableMarginBalance();
  }, [resetAvailableMarginBalance]);

  const rows = useMemo(
    (): ReportValueCardProps[] => [
      {
        label: StrategyReportThemedCardValuesLabels.MAX_STOCK_DROP,
        value: maxStockDrop,
        type: SingleValueCardType.PERCENTAGE,
        onValueChange: setMaxStockDrop,
      },
      {
        label: StrategyReportThemedCardValuesLabels.OEA_DROP,
        value: oeaDrop,
        type: SingleValueCardType.PERCENTAGE,
        onValueChange: setOeaDrop,
      },
      {
        label: StrategyReportThemedCardValuesLabels.COLLAR_PL,
        value: collarPL,
        type: SingleValueCardType.CURRENCY,
        onValueChange: setCollarPL,
      },
      {
        label: StrategyReportThemedCardValuesLabels.AVAILABLE_MARGIN_BALANCE,
        // No figure until the operator types one -- null, which the card
        // renders as the muted N/A and the export drops -- and then their
        // figure, in the figure treatment and on the client's copy. The value
        // says which of the two it is on its own, so nothing rides beside it.
        ...availableMarginBalanceRow,
        type: SingleValueCardType.CURRENCY,
        onValueChange: handleAvailableMarginBalanceChange,
        // Emptying the box is the way back to N/A, and this row is the only
        // one on the report that has a way back to want: every other field
        // here is persisted, where clearing is a slip rather than an
        // instruction. Same call as the per-input-change reset above, because
        // it is the same thing -- nobody has entered a balance.
        onClear: resetAvailableMarginBalance,
      },
    ],
    [
      maxStockDrop,
      oeaDrop,
      collarPL,
      availableMarginBalanceRow,
      setMaxStockDrop,
      setOeaDrop,
      setCollarPL,
      handleAvailableMarginBalanceChange,
      resetAvailableMarginBalance,
    ],
  );

  const card = useMemo(
    (): ReportThemedCardConfig => ({
      label: StrategyReportThemedCardLabels.PROTECT,
      description: StrategyReportThemedCardDescriptions.PROTECT,
      values: rows,
      // Which asset's max_stock_drop the card (and its drawdown chart)
      // shows.
      headerAction: protectDrops ? (
        <HeaderAssetSelect
          value={selectedProtectAsset}
          options={Object.keys(protectDrops)}
          onChange={handleProtectAssetChange}
        />
      ) : undefined,
      addictional_component: {
        type: PDFReportChartType.BAR,
        title: DRAWDOWN_CHART_TITLE,
        limit: maxDropLimit,
        negativeLimit: negativeDropLimit,
        // A row with no figure is sent as "No Report", never zeroed: the
        // renderer prints the words beside an empty track, as the screen
        // chart does, so an unrun report's paper says "No Report" rather
        // than leaving the chart out or drawing a 0.0% bar. The readings
        // are taken once above so the paper and the screen cannot disagree
        // about which rows had a figure.
        data: [
          {
            label: CHART_SERIES_STOCK,
            value: maxStockDropPct ?? NO_REPORT_DISPLAY,
            type: PDFReportValueType.PERCENTAGE,
          },
          {
            label: CHART_SERIES_OEA,
            value: oeaDropPct ?? NO_REPORT_DISPLAY,
            type: PDFReportValueType.PERCENTAGE,
          },
        ],
      },
    }),
    [
      rows,
      protectDrops,
      selectedProtectAsset,
      handleProtectAssetChange,
      maxDropLimit,
      negativeDropLimit,
      maxStockDropPct,
      oeaDropPct,
    ],
  );

  return {
    card,
    maxDropLimits,
    setMaxDropLimits,
    applyRun,
    resetForNewInputs,
  };
};
